import { ArrowLeft, ChevronDown, Download } from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { Badge, WorkspaceSkeleton } from '../components/UI'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  loadEmployeeHistory,
  loadOrganisationHistory,
  loadPersonOperations,
  readableDataError,
  shortThreadId,
  type DailyOperatingSummary,
  type EmployeeDailyRecord,
  type TeamPerson
} from '../lib/liveOperations'

type Period = 'daily' | 'weekly' | 'monthly'
type Scope = 'organisation' | 'employee'
const dayKey = (value: string) => String(value).slice(0, 10)

function monthKey(value: string) { return dayKey(value).slice(0, 7) }
function weekOfMonth(value: string) { return Math.floor((Number(dayKey(value).slice(8, 10)) - 1) / 7) + 1 }
function weekKey(value: string) { return `${monthKey(value)}-W${weekOfMonth(value)}` }

function dayLabel(value: string) {
  return new Date(`${dayKey(value)}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  })
}

function monthLabel(value: string) {
  const [year, month] = value.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

function periodLabel(period: Period, key: string) {
  if (period === 'daily') return dayLabel(key)
  if (period === 'weekly') return `Week ${Number(key.split('-W')[1])} · ${monthLabel(key.slice(0, 7))}`
  return monthLabel(key)
}

function matchesPeriod(value: string, period: Period, key: string) {
  if (period === 'daily') return dayKey(value) === key
  if (period === 'weekly') return weekKey(value) === key
  return monthKey(value) === key
}

function average(values: Array<number | null | undefined>) {
  const numbers = values.filter((value): value is number => value != null && Number.isFinite(value))
  return numbers.length ? Math.round(numbers.reduce((sum, value) => sum + value, 0) / numbers.length) : null
}

function OrganisationReport({ rows }: { rows: DailyOperatingSummary[] }) {
  const received = rows.reduce((sum, row) => sum + Number(row.inbound_threads || 0), 0)
  const replies = rows.reduce((sum, row) => sum + Number(row.threads_replied || 0), 0)
  const worked = rows.reduce((sum, row) => sum + Number(row.threads_worked || 0), 0)
  const people = Math.max(0, ...rows.map(row => Number(row.active_people || 0)))

  return <>
    <div className="history-detail-kpis">
      <div><span>Customer emails received</span><strong>{received}</strong></div>
      <div><span>Replies sent</span><strong>{replies}</strong></div>
      <div><span>Customer emails worked</span><strong>{worked}</strong></div>
      <div><span>Employees active</span><strong>{people}</strong></div>
      <div><span>Average response</span><strong>{formatDuration(average(rows.map(row => row.average_arrival_to_reply_sent_seconds)))}</strong></div>
      <div><span>Response rate</span><strong>{received ? `${Math.round((replies / received) * 100)}%` : '—'}</strong></div>
    </div>

    <div className="history-detail-days">
      {[...rows].sort((a,b) => dayKey(b.operating_day).localeCompare(dayKey(a.operating_day))).map(row => (
        <article key={row.operating_day}>
          <div><strong>{dayLabel(row.operating_day)}</strong><span>{row.inbound_threads} customer emails received · {row.threads_replied} replies sent</span></div>
          <div><span>Active employees</span><strong>{row.active_people}</strong></div>
          <div><span>Average response</span><strong>{formatDuration(row.average_arrival_to_reply_sent_seconds)}</strong></div>
          <div><span>Response rate</span><strong>{row.inbound_threads ? `${Math.round((Number(row.threads_replied || 0) / Number(row.inbound_threads)) * 100)}%` : '—'}</strong></div>
        </article>
      ))}
    </div>
  </>
}

function EmployeeReport({ records }: { records: EmployeeDailyRecord[] }) {
  const replied = records.filter(record => record.work_status === 'replied').length
  const byDay = new Map<string, EmployeeDailyRecord[]>()
  for (const record of records) {
    const key = dayKey(record.activity_day)
    byDay.set(key, [...(byDay.get(key) ?? []), record])
  }

  return <>
    <div className="history-detail-kpis">
      <div><span>Customer emails worked</span><strong>{records.length}</strong></div>
      <div><span>Replies sent</span><strong>{replied}</strong></div>
      <div><span>Needs follow-through</span><strong>{records.length - replied}</strong></div>
      <div><span>Response rate</span><strong>{records.length ? `${Math.round((replied / records.length) * 100)}%` : '—'}</strong></div>
      <div><span>Average response</span><strong>{formatDuration(average(records.map(record => record.arrival_to_reply_sent_seconds)))}</strong></div>
    </div>

    <div className="history-detail-email-days">
      {[...byDay.entries()].sort(([a],[b]) => b.localeCompare(a)).map(([day, dayRecords]) => (
        <details key={day} open>
          <summary>
            <div><strong>{dayLabel(day)}</strong><span>{dayRecords.length} customer email{dayRecords.length === 1 ? '' : 's'}</span></div>
            <ChevronDown size={17}/>
          </summary>
          <div className="history-detail-email-list">
            {dayRecords.map(record => (
              <div key={`${day}:${record.external_thread_id}`}>
                <div><strong>Email reference {shortThreadId(record.external_thread_id)}</strong><span>{record.inbound_received_at ? `Received ${new Date(record.inbound_received_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}` : 'Received time unavailable'}</span></div>
                <div><span>Response time</span><strong>{formatDuration(record.arrival_to_reply_sent_seconds)}</strong></div>
                <div><span>Reply status</span><strong>{record.reply_sent_at ? new Date(record.reply_sent_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : 'Waiting'}</strong></div>
                <Badge tone={record.work_status === 'replied' ? 'good' : 'neutral'}>{record.work_status === 'replied' ? 'Replied' : 'Needs follow-through'}</Badge>
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  </>
}

export function HistoryReportDetail() {
  const { selectedTeamId, selectedTeam } = useTeamScope()
  const { scope, period, subjectId, recordKey } = useParams()
  const validScope: Scope | null = scope === 'organisation' || scope === 'employee' ? scope : null
  const validPeriod: Period | null = period === 'daily' || period === 'weekly' || period === 'monthly' ? period : null

  const [organisationRows, setOrganisationRows] = useState<DailyOperatingSummary[]>([])
  const [employeeRows, setEmployeeRows] = useState<EmployeeDailyRecord[]>([])
  const [person, setPerson] = useState<TeamPerson | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!validScope || !validPeriod || !recordKey || !subjectId) return

    if (validScope === 'organisation') {
      loadOrganisationHistory(selectedTeamId)
        .then(rows => setOrganisationRows(rows.filter(row => matchesPeriod(row.operating_day, validPeriod, recordKey))))
        .catch(err => setError(readableDataError(err, 'This organisation report could not be loaded.')))
        .finally(() => setLoading(false))
      return
    }

    Promise.all([loadEmployeeHistory(subjectId), loadPersonOperations(subjectId, selectedTeamId)])
      .then(([records, personData]) => {
        setEmployeeRows(records.filter(record => matchesPeriod(record.activity_day, validPeriod, recordKey)))
        setPerson(personData.person)
      })
      .catch(err => setError(readableDataError(err, 'This employee report could not be loaded.')))
      .finally(() => setLoading(false))
  }, [validScope, validPeriod, recordKey, subjectId, selectedTeamId])

  const title = useMemo(() => validPeriod && recordKey ? periodLabel(validPeriod, recordKey) : 'History report', [validPeriod, recordKey])

  if (!validScope || !validPeriod || !recordKey || !subjectId) return <Navigate to="/app/history" replace/>
  if (loading) return <WorkspaceSkeleton rows={6}/>

  return <div className="stack-lg history-detail-page">
    <div className="detail-page-nav">
      <Link to="/app/history" className="back-link"><ArrowLeft size={17}/>Back to History</Link>
    </div>

    <section className="history-detail-hero">
      <div>
        <span className="eyebrow">{validScope === 'organisation' ? `${selectedTeam?.name ?? 'Organisation'} ${validPeriod} report` : `${person?.displayName ?? 'Employee'} · ${validPeriod} report`}</span>
        <h2>{title}</h2>
        <p>{validScope === 'organisation' ? `${selectedTeam ? `${selectedTeam.name}’s` : 'The organisation’s'} recorded customer-email operating result for this period.` : `The customer-email work Mail Percent recorded for ${person?.displayName ?? 'this employee'} during this period.`}</p>
      </div>
      <button className="button secondary" onClick={() => window.print()}><Download size={16}/>Export report</button>
    </section>

    {error && <div className="notice notice-inline"><strong>Report unavailable:</strong><span>{error}</span></div>}

    {!error && validScope === 'organisation' && organisationRows.length === 0 && <section className="records-empty"><strong>No data for this report</strong><span>No organisation activity was recorded for this period.</span></section>}
    {!error && validScope === 'employee' && employeeRows.length === 0 && <section className="records-empty"><strong>No data for this report</strong><span>No employee customer-email activity was recorded for this period.</span></section>}

    {validScope === 'organisation' && organisationRows.length > 0 && <OrganisationReport rows={organisationRows}/>}
    {validScope === 'employee' && employeeRows.length > 0 && <EmployeeReport records={employeeRows}/>}
  </div>
}
